import { BaseModel } from '../BaseModel'
import adminApiClient from '../../api/admin-api-client'
import authApiClient from '../../api/auth-api-client'

/**
 * Class User
 *
 * @extends BaseModel
 */
export class User extends BaseModel {
  constructor(params) {
    super(params)
    this.ReadOnly = params.ReadOnly || false
    this.userDbId = params.userDbId || 0
    this.companyId = params.companyId || 0
    this.endpointId = params.endpointId || null
    this.groupId = params.groupId > 0 ? params.groupId : 3 //default to portalUser
    this.userId = params.userId || ''
    this.userName = params.userName || ''
    this.phoneNumber = params.phoneNumber || null
    this.timestampUTC = params.timestampUTC || ''
    this.authHash = params.authHash || ''
    this.hasImage = params.hasImage || false
    this.otpEnabled = params.otpEnabled || false
    this.entityContact = params.entityContact || null
    this.contactPhone = params.contactPhone || null
    this.commercialPhone = params.commercialPhone || null
    this.email = params.email || null
    this.invoicingEmail = params.invoicingEmail || null
    this.trainingEmail = params.trainingEmail || null
    this.primaryAddress = params.primaryAddress || null
    this.secondaryAddress = params.secondaryAddress || null
    this.invoicingAddress = params.invoicingAddress || null

    /**
     * @property {Company} company
     */

    this.company = params.company || null
    /**
     * @property {Group} group
     */
    this.group = params.group || null
    /**
     * @property {Endpoint} endpoint
     */
    this.endpoint = params.endpoint || null
    this.active = params.active || false
    this.isReadOnly = params.isReadOnly || false
    this.password = params.password || ''
    /**
     * @property {ApplicationCollection} applications
     *
     */
    this.applications = params.applications || [] // array is temporary solution
    /**
     * @property {PropertyCollection} properties
     */
    this.properties = params.properties || [] // array is temporary solution
    this.features = params.features || null // todo: user feature class
    /**
     * @property {SubdivisionCollection} subdivisions
     */
    this.subdivisions = params.subdivisions || [] // array is temporary solution

    /**
     * if user's main company is different than the requested company
     * this is set to true
     */
    this.implicit = params.implicit !== undefined ? params.implicit : false // ??params.implicit

    this.externalCompanyUser = params.externalCompanyUser || ''

    this.has2fa = false // 2fa enabled?

    this.defaultMail = params.defaultMail || 'None' //Which email address is used for email (CompanyMail/UserMail/'')

    this.entityAddress = params.entityAddress || null

    this.entityAddress = params.entityAddress || null

    this.entityAddress = params.entityAddress || null

    /**
     * Save the initial state of the object for comparing changes
     */
    this._initialValues = { ...this, _initialValues: null }
  }

  get licences() {
    return this.applications.map((a) => a.applicationDescription).join(', ')
  }

  /**
   * PUT /api/users
   * @override
   *
   * @returns {Promise}
   */
  async update() {
    this.isLoading = true

    // Fetch current entity contacts to get the IDs
    const entityContactsResponse = await adminApiClient.getUsersEntityContacts(this.userDbId)

    // Map the contact IDs from the response
    const contactIds = {}
    entityContactsResponse.data.forEach((contact) => {
      switch (contact.contactTypeId) {
        case 1:
          contactIds.phoneId = contact.contactId
          break
        case 2:
          contactIds.commercialPhoneId = contact.contactId
          break
        case 3:
          contactIds.emailId = contact.contactId
          break
        case 4:
          contactIds.invoicingEmailId = contact.contactId
          break
        case 5:
          contactIds.trainingEmailId = contact.contactId
          break
      }
    })

    // Compare initial and current entity contact values
    const changedContacts = []
    if (this.entityContact && this._initialValues.entityContact) {
      const contactFields = ['phone', 'commercialPhone', 'email', 'invoicingEmail', 'trainingEmail']
      contactFields.forEach((field, index) => {
        if (this.entityContact[field] !== this._initialValues.entityContact[field]) {
          changedContacts.push({
            contactTypeId: index + 1,
            value: this.entityContact[field],
            contactId: contactIds[`${field}Id`],
          })
        }
      })
    }

    // Update changed contacts first
    for (const contact of changedContacts) {
      if (!contact.value) {
        // Delete contact if value is empty and contactId exists
        if (contact.contactId) {
          await adminApiClient.deleteEntityContact(contact.contactId)
        }
      } else if (contact.contactId) {
        // Update existing contact
        await adminApiClient.updateEntityContact(contact.contactId, {
          entityTypeId: 3,
          contactTypeId: contact.contactTypeId,
          contact: contact.value,
          entityId: this.userDbId,
        })
      } else {
        // Create new contact
        await adminApiClient.createEntityContact({
          entityTypeId: 3,
          contactTypeId: contact.contactTypeId,
          contact: contact.value,
          entityId: this.userDbId,
        })
      }
    }

    // Handle entity addresses
    if (this.entityAddress && this._initialValues.entityAddress) {
      const addressTypeMap = {
        1: 'primaryAddress',
        2: 'secondaryAddress',
        3: 'invoicingAddress',
      }

      // Get current addresses to map IDs
      const entityAddressesResponse = await adminApiClient.getUsersEntityAddresses(this.userDbId)
      const existingAddresses = {}
      entityAddressesResponse.data.forEach((address) => {
        existingAddresses[addressTypeMap[address.addressTypeId]] = address
      })

      // Update or create addresses
      for (const addressTypeId of Object.keys(addressTypeMap)) {
        const newAddress = this.entityAddress[addressTypeMap[addressTypeId]]
        const existingAddress = existingAddresses[addressTypeMap[addressTypeId]]
        const initialAddress = this._initialValues.entityAddress[addressTypeMap[addressTypeId]]

        if (!newAddress && existingAddress) {
          // Delete address if it was removed
          await adminApiClient.deleteEntityAddress(existingAddress.addressId)
        } else if (newAddress && !existingAddress) {
          // Create new address
          await adminApiClient.createEntityAddress({
            entityTypeId: 3,
            entityId: this.userDbId,
            addressTypeId: addressTypeId,
            ...newAddress,
          })
        } else if (newAddress && existingAddress && JSON.stringify(newAddress) !== JSON.stringify(initialAddress)) {
          // Update existing address if changed
          await adminApiClient.updateEntityAddress(existingAddress.addressId, {
            entityTypeId: 3,
            entityId: this.userDbId,
            addressTypeId: addressTypeId,
            ...newAddress,
          })
        }
      }
    }

    const userToSave = { ...this }
    userToSave.company = null
    userToSave.endpoint = null
    userToSave.group = null
    userToSave.authHash = null
    userToSave.timestampUTC = null
    userToSave.hasImage = undefined
    userToSave.entityContact = null
    userToSave.entityAddress = null

    return adminApiClient
      .updateUser(this.userDbId, userToSave)
      .then((response) => {
        if (response.status === 200) {
          this.isLoading = false
          return true
        } else {
          throw new Error('Failed to save user')
        }
      })
      .catch((ex) => {
        this.isLoading = false
        throw ex
      })
  }

  is2FaEnabled() {
    if (this.userDbId === 0) {
      return
    }

    return authApiClient
      .is2FAEnabled(this.userDbId)
      .then((response) => {
        this.has2fa = response.data
      })
      .catch((ex) => {
        console.error(ex)
      })
  }

  remove2fa() {
    if (this.userDbId === 0) {
      return
    }
    return authApiClient
      .remove2FA(this.userDbId)
      .then(() => {
        this.has2fa = false
      })
      .catch((ex) => {
        throw ex
      })
  }

  /**
   * POST /api/Users/resendsetpasswordemail
   *
   * Mails the user a fresh one-time link to set their own password. Any link sent
   * earlier stops working.
   *
   * @returns {Promise}
   */
  resendSetPasswordEmail() {
    if (!this.userDbId) {
      return Promise.resolve()
    }
    return adminApiClient.resendSetPasswordEmail(this.userDbId)
  }

  transformForComparison(model) {
    const onlyValues = (props) => {
      return props
        .map((prop) => {
          return {
            propertyValue: prop.propertyValue ? prop.propertyValue.toString().toLowerCase() : prop.propertyValue,
          }
        })
        .filter((prop) => !!prop.propertyValue)
    }
    const reduceApplications = (apps) => {
      return apps.map((app) => {
        return {
          appId: app.appId,
          applicationDescription: app.applicationDescription,
        }
      })
    }
    model.applications = reduceApplications(model.applications)
    model.properties = onlyValues(model.properties)
    return model
  }
  /**
    {
      "userDbId": 0,
      "toCompanyId": 0,
      "toEndpointId": 0,
      "toSubdivisionId": 0,
      "externalCompanyUser": "string"
    }
  * 
  * @param {*} moveObject 
  */
  changeUserCompany(moveObject) {
    return adminApiClient.changeUserCompany(moveObject)
  }

  /**
   * GET /api/Users/{userid}/exists
   *
   * Checks if the userId(email) exists
   *
   * @param {string} userId email
   * @returns {Promise}
   */
  static isUserIdAvailable(userId) {
    return adminApiClient.userExists(userId)
  }
}
